import { useCallback, useEffect, useLayoutEffect, useRef, type RefObject } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';
import { SCROLL_TO_TOP_EVENT } from './scrollPageToTop';
import { SOFT_REFRESH_EVENT } from '@/components/common/PullToRefresh';
import { patchFavoriteSnapshot, type FavoriteSnapshotChange } from './listSnapshotFavorites';

/**
 * 목록 복원 — 목록에서 글을 보고 뒤로 왔을 때 "보던 목록·페이지·스크롤 위치" 그대로 돌아오게 한다.
 *
 * 목록과 상세가 형제 라우트라 상세로 가면 목록 컴포넌트가 완전히 사라지고, 돌아오면 빈 화면에서 1페이지부터 다시 받는다.
 * 그래서 브라우저 기본 스크롤 복원이 어긋나고, 무한 스크롤 2페이지 이후의 글로는 아예 못 돌아온다.
 *
 * 방식: 방문 기록 한 칸(`location.key`)마다 목록 상태와 스크롤 위치를 메모리에 기억해 두고,
 * **뒤로가기(POP)로 돌아왔을 때만** 다시 받지 않고 그대로 복원한다. 새로 들어온 경우(PUSH/REPLACE)는 평소처럼 처음부터 받는다.
 *
 * - 메모리에만 둔다(세션 저장소 X) — 앱을 다시 띄우면 처음부터 보는 게 자연스럽고, 용량·묵은 데이터 문제가 없다.
 * - 복원된 목록은 떠날 때 모습 그대로다. 조회수·새 글은 당겨서 새로고침(해당 화면 저장본 삭제)으로 갱신한다.
 * - 로그인 상태가 바뀌거나 차단·삭제처럼 목록 내용이 달라지는 동작 뒤에는 [clearListSnapshots] 로 전부 버린다.
 */

interface Snapshot {
    data: unknown;
    savedAt: number;
    needsRefresh?: boolean;
}

/** 저장본 유지 시간 — 이보다 오래된 목록은 복원하지 않고 새로 받는다 */
const SNAPSHOT_TTL_MS: number = 10 * 60 * 1000;
/** 기억하는 목록 수 상한(오래된 것부터 버림) — 홈은 한 화면에 목록이 3개라 넉넉히 둔다 */
const MAX_SNAPSHOTS: number = 60;
/** 기억하는 스크롤 위치 수 상한 */
const MAX_SCROLL_ENTRIES: number = 40;
/** 복원한 스크롤 위치가 이 오차 안이면 맞춘 것으로 본다 */
const SCROLL_TOLERANCE_PX: number = 2;

const snapshots: Map<string, Snapshot> = new Map();
const scrollPositions: Map<string, number> = new Map();
/** 가로 넘김 목록(홈 카드 넘김) 위치 — `location.key::목록 이름` 마다 scrollLeft 와 적은 시각 */
const horizontalPositions: Map<string, { left: number; savedAt: number }> = new Map();
/** 지금 화면의 location.key — 당겨서 새로고침 때 이 화면 저장본만 지우는 데 쓴다 */
let activeLocationKey: string | null = null;

const snapshotKey = (locationKey: string, listId: string): string => `${locationKey}::${listId}`;

const trim = <V>(map: Map<string, V>, max: number): void => {
    while (map.size > max) {
        const oldest: string | undefined = map.keys().next().value;
        if (oldest === undefined) return;
        map.delete(oldest);
    }
};

const clearForLocation = (locationKey: string): void => {
    const prefix: string = `${locationKey}::`;
    Array.from(snapshots.keys())
        .filter((key: string) => key.startsWith(prefix))
        .forEach((key: string) => snapshots.delete(key));
    scrollPositions.delete(locationKey);
    Array.from(horizontalPositions.keys())
        .filter((key: string) => key.startsWith(prefix))
        .forEach((key: string) => horizontalPositions.delete(key));
};

/** 모든 목록 저장본을 버린다 — 로그인/로그아웃, 사용자 차단, 글 삭제처럼 목록 내용이 달라지는 동작 뒤에 호출한다. */
export function clearListSnapshots(): void {
    snapshots.clear();
    scrollPositions.clear();
    horizontalPositions.clear();
}

/** 관심 변경은 기존 목록·페이지·스크롤을 지우지 않고 표시만 갱신한다. */
export function syncFavoriteListSnapshots(change: FavoriteSnapshotChange): void {
    snapshots.forEach((snapshot, key) => {
        snapshots.set(key, {
            ...snapshot,
            data: patchFavoriteSnapshot(snapshot.data, change),
            // 관심 치과 화면은 포함되는 치과·공고 자체가 바뀐다. 복원한 내용을 보여 주면서 다시 받는다.
            needsRefresh: snapshot.needsRefresh || (change.kind === 'clinic' && key.endsWith('::favorites:clinics')),
        });
    });
}

if (typeof window !== 'undefined') {
    // 당겨서 새로고침: 라우트가 remount 되기 전에(이벤트는 동기 전달) 이 화면 저장본을 지워 새로 받게 한다
    window.addEventListener(SOFT_REFRESH_EVENT, () => {
        if (activeLocationKey !== null) clearForLocation(activeLocationKey);
    });
}

/**
 * 목록 상태 복원 훅.
 *
 * @param listId 화면 안에서 목록을 구분하는 이름. 필터·검색어가 다르면 다른 목록이므로 그 값까지 넣는다(예: `table:/posts?search=임플란트`)
 * @returns `restored` — 뒤로가기로 돌아왔고 유효한 저장본이 있으면 그 값, 아니면 null(평소처럼 새로 받을 것).
 *          `needsRefresh` — 저장된 내용은 먼저 보여 주고 백그라운드로 구성을 갱신해야 하는지(관심치과 목록).
 *          `save` — 목록 상태가 바뀔 때마다 호출해 최신 상태를 기억시킨다(참조만 보관하므로 가볍다)
 *
 * @example
 * const { restored, save } = useListRestore<{ items: Item[]; page: number }>('seminars');
 * const [items, setItems] = useState<Item[]>(restored?.items ?? []);
 * useEffect(() => save({ items, page }), [items, page, save]);
 */
export function useListRestore<T>(listId: string): { restored: T | null; needsRefresh: boolean; save: (data: T) => void } {
    const location = useLocation();
    const navigationType = useNavigationType();
    const key: string = snapshotKey(location.key, listId);
    const resolvedRef = useRef<{ value: T | null; needsRefresh: boolean } | null>(null);

    // 마운트 시점에 한 번만 판정한다. 목록 화면이 필터를 URL 에 맞추느라 같은 화면에서 replace 를 하면 location.key 가 바뀌는데,
    // 그때 "복원 여부"가 뒤집히면 복원 중에 다시 받거나 자동 스크롤이 끼어든다. 저장(save)만 새 key 를 따라간다.
    if (resolvedRef.current === null) {
        const snapshot: Snapshot | undefined = snapshots.get(key);
        const usable: boolean = navigationType === 'POP' && snapshot !== undefined && Date.now() - snapshot.savedAt <= SNAPSHOT_TTL_MS;
        if (!usable) snapshots.delete(key);
        resolvedRef.current = { value: usable && snapshot ? (snapshot.data as T) : null, needsRefresh: usable && snapshot?.needsRefresh === true };
    }

    const save = useCallback(
        (data: T): void => {
            snapshots.delete(key);
            snapshots.set(key, { data, savedAt: Date.now() });
            trim(snapshots, MAX_SNAPSHOTS);
        },
        [key],
    );

    return { restored: resolvedRef.current.value, needsRefresh: resolvedRef.current.needsRefresh, save };
}

/** 복원 직후 높이가 늦게 자리 잡는 경우(이미지·폰트)를 위해 위치를 다시 맞춰 보는 시점들(ms) */
const SCROLL_RECHECK_DELAYS_MS: readonly number[] = [120, 400];

/**
 * 화면 스크롤 위치 기억·복원 훅 — 화면(라우트)당 한 곳에서만 쓴다.
 *
 * 스크롤할 때마다 위치를 기억해 두고(떠나는 순간에 읽으면 화면이 이미 접혀 0 이 나온다),
 * 뒤로가기로 돌아와 복원된 목록이 그려진 직후 기억해 둔 위치로 되돌린다.
 *
 * @param ready 복원된 내용이 첫 렌더에 그려지는지 — **마운트 시점에 확정된 값**이어야 한다(저장본은 동기적으로 읽힌다).
 *              false 면(저장본 없음·만료) 복원하지 않고 평소처럼 스크롤만 기록한다
 */
export function useScrollRestore(ready: boolean): void {
    const location = useLocation();
    const navigationType = useNavigationType();
    const locationKey: string = location.key;
    /** 마운트 시점의 복원 목표. 이후 같은 화면에서 URL replace 로 key 가 바뀌어도 목표는 그대로다 */
    const targetRef = useRef<number | undefined>(navigationType === 'POP' && ready ? scrollPositions.get(locationKey) : undefined);
    /** 복원이 끝나기 전에는 브라우저가 멋대로 옮긴 위치(접힌 화면의 0 등)를 기억하지 않는다 */
    const restoringRef = useRef<boolean>(targetRef.current !== undefined);

    // 리스너 등록·해제는 layout effect 로 — 떠날 때 커밋 단계에서 즉시 해제돼, 화면이 접히며 생기는 스크롤 이벤트를 기록하지 않는다
    useLayoutEffect(() => {
        activeLocationKey = locationKey;
        const record = (): void => {
            scrollPositions.delete(locationKey);
            scrollPositions.set(locationKey, window.scrollY);
            trim(scrollPositions, MAX_SCROLL_ENTRIES);
        };
        // 같은 화면에서 URL replace 로 key 가 바뀐 경우 — 현재 위치를 새 key 로 이어 적는다(스크롤 없이 바로 상세로 가도 돌아올 수 있게)
        if (!restoringRef.current) record();
        const onScroll = (): void => {
            if (restoringRef.current) return;
            record();
        };
        window.addEventListener('scroll', onScroll, { passive: true });
        // 탭(클릭) 순간에도 한 번 적는다 — 글을 누르면 곧바로 화면이 바뀌므로, 스크롤 이벤트가 아직 안 온 경우(스크롤 직후 탭)에도 정확한 위치가 남는다.
        // capture 단계라 화면 전환(핸들러 실행)보다 먼저 실행되고, 이때는 목록이 아직 그대로라 scrollY 가 유효하다.
        window.addEventListener('click', onScroll, { capture: true, passive: true });
        return () => {
            window.removeEventListener('scroll', onScroll);
            window.removeEventListener('click', onScroll, { capture: true });
            if (activeLocationKey === locationKey) activeLocationKey = null;
        };
    }, [locationKey]);

    // 복원은 마운트 때 한 번 — 복원된 목록이 첫 렌더에 그려진 직후(자식들의 layout effect 뒤)에 실행된다
    useLayoutEffect(() => {
        const target: number | undefined = targetRef.current;
        if (target === undefined) return undefined;
        let userMoved: boolean = false;
        const onUserMove = (): void => {
            userMoved = true;
        };
        window.addEventListener('touchstart', onUserMove, { passive: true });
        window.addEventListener('wheel', onUserMove, { passive: true });
        window.addEventListener(SCROLL_TO_TOP_EVENT, onUserMove);
        window.scrollTo(0, target);
        const timers: number[] = SCROLL_RECHECK_DELAYS_MS.map((delay: number, index: number) =>
            window.setTimeout(() => {
                // 사용자가 이미 움직이기 시작했으면 손대지 않는다
                if (!userMoved && Math.abs(window.scrollY - target) > SCROLL_TOLERANCE_PX) {
                    window.scrollTo(0, target);
                }
                if (index === SCROLL_RECHECK_DELAYS_MS.length - 1) finish(true);
            }, delay),
        );
        /** @param recordNow 복원 구간 동안 기록을 쉬었으므로, 정상 종료 시 현재 위치를 한 번 적어 둔다(떠나는 중에는 화면이 접혀 있어 적지 않는다) */
        const finish = (recordNow: boolean): void => {
            restoringRef.current = false;
            window.removeEventListener('touchstart', onUserMove);
            window.removeEventListener('wheel', onUserMove);
            window.removeEventListener(SCROLL_TO_TOP_EVENT, onUserMove);
            if (recordNow && activeLocationKey !== null) {
                scrollPositions.delete(activeLocationKey);
                scrollPositions.set(activeLocationKey, window.scrollY);
            }
        };
        return () => {
            timers.forEach((timer: number) => window.clearTimeout(timer));
            finish(false);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
}

/** 가로 위치 복원을 시도하는 시간 — 홈 목록이 늦게 그려지거나, 부품이 처음에 첫 장으로 돌리는 동작을 이 안에서 다시 덮는다 */
const HORIZONTAL_RESTORE_MS: number = 1200;

/** 위치를 맞추느라 지금 숨겨 둔 넘김 영역들 */
const hiddenHorizontalTracks: Set<HTMLElement> = new Set<HTMLElement>();

/**
 * 가로 넘김 영역을 숨긴 채 위치를 맞추는 중인지 — 밀어서 뒤로가기(SwipeBack)는 이 동안 이전 화면 복사본을 걷지 않는다.
 * 복사본을 먼저 걷으면 보이던 카드가 숨김 동안 빈칸이 됐다가 다시 나타나 깜박였다(2026-11-03 실기기, 뒤로가기 버튼은 정상)
 */
export const isHorizontalRestorePending = (): boolean => hiddenHorizontalTracks.size > 0;

/**
 * 가로 넘김 목록 위치 기억·복원 훅 — 홈 카드 넘김에서 2번째 장의 글을 보고 뒤로 오면 1번째 장부터 다시 보이던 문제(2026-11-03).
 *
 * 넘길 때마다 scrollLeft 를 기억해 두고, **뒤로가기(POP)로 돌아왔을 때만** 그 위치로 되돌린다. 저장본 유지 시간은 목록 저장본과 같다.
 * 넘김 부품들은 마운트·내용 변경 때 첫 장으로 돌리고(`scrollTo(0)`), 내용이 늦게 그려지기도 해서, 복원 시간 동안 프레임마다 위치를 확인해 다시 맞춘다.
 * 사용자가 그 목록을 만지면 바로 손을 뗀다.
 *
 * @param trackRef 가로로 스크롤되는 요소. 처음엔 없다가(목록이 비어 있음) 나중에 생겨도 된다
 * @param listId 화면 안에서 목록을 구분하는 이름(예: `home:premium-mobile`)
 * @returns 되살릴 scrollLeft(되살리지 않으면 0) — 부품은 이 값이 0 보다 크면 마운트 때 첫 장으로 되돌리지 않는다
 */
export function useHorizontalScrollRestore(trackRef: RefObject<HTMLElement>, listId: string): number {
    const location = useLocation();
    const navigationType = useNavigationType();
    const key: string = snapshotKey(location.key, listId);
    const targetRef = useRef<number | undefined>(undefined);
    if (targetRef.current === undefined) {
        const saved = horizontalPositions.get(key);
        const usable: boolean = navigationType === 'POP' && saved !== undefined && Date.now() - saved.savedAt <= SNAPSHOT_TTL_MS;
        targetRef.current = usable && saved ? saved.left : 0;
    }

    /** 복원 중인지 — 마운트 때(레이아웃 단계) 정하고, 위치가 자리 잡거나 사용자가 만지거나 시간이 다 되면 끈다 */
    const restoringRef = useRef<boolean>((targetRef.current ?? 0) > 0);
    /** 복원하는 동안 숨겨 둔 요소 — 자리 잡으면 다시 보인다 */
    const hiddenRef = useRef<HTMLElement | null>(null);
    /** 넘길 만큼 내용이 그려졌으면 목표 위치로 맞춘다(덜 그려진 상태에서 맞추면 끝에 걸린다). 맞췄으면 true */
    const applyTarget = useCallback((): boolean => {
        const target: number = targetRef.current ?? 0;
        const el: HTMLElement | null = trackRef.current;
        if (!el || target <= 0) return false;
        if (el.scrollWidth - el.clientWidth < target - SCROLL_TOLERANCE_PX) return false;
        if (Math.abs(el.scrollLeft - target) > SCROLL_TOLERANCE_PX) el.scrollLeft = target;
        return true;
    }, [trackRef]);
    /** 복원하는 동안은 넘김 영역을 숨긴다 — 아이폰(WebKit)은 가로 스크롤 위치를 따로 그려서 코드로 옮긴 위치가 한 프레임 늦게 보여,
     *  첫 장이 잠깐 보였다가 넘어갔다(2026-11-03 실기기). 자리 잡은 걸 확인한 뒤 보인다 */
    const hide = useCallback((): void => {
        const el: HTMLElement | null = trackRef.current;
        if (!el || hiddenRef.current === el) return;
        if (hiddenRef.current) hiddenHorizontalTracks.delete(hiddenRef.current);
        el.style.visibility = 'hidden';
        hiddenRef.current = el;
        hiddenHorizontalTracks.add(el);
    }, [trackRef]);
    const reveal = useCallback((): void => {
        if (hiddenRef.current) {
            hiddenRef.current.style.visibility = '';
            hiddenHorizontalTracks.delete(hiddenRef.current);
        }
        hiddenRef.current = null;
    }, []);

    // 첫 화면을 그리기 전에 숨기고 맞춘다 — effect 에서 맞추면 첫 장이 한두 프레임 보였다가 넘어가 깜박였다
    useLayoutEffect(() => {
        if (!restoringRef.current) return;
        hide();
        applyTarget();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        let frame: number = 0;
        /** 목표 위치에 연속으로 머문 프레임 수 — 2 프레임 머물면 화면에도 반영된 것으로 보고 보인다 */
        let settledFrames: number = 0;
        const record = (): void => {
            const el: HTMLElement | null = trackRef.current;
            if (!el) return;
            horizontalPositions.delete(key);
            horizontalPositions.set(key, { left: el.scrollLeft, savedAt: Date.now() });
            trim(horizontalPositions, MAX_SCROLL_ENTRIES);
        };
        // scroll 은 거품이 안 올라오므로 capture 로 받아 이 목록 것만 본다(요소가 나중에 생겨도 놓치지 않는다).
        // 복원 중에 부품이 첫 장으로 돌리면(마운트·내용 변경 때 scrollTo(0)) 이 이벤트 안에서 바로 되돌린다
        const onScroll = (event: Event): void => {
            if (event.target !== trackRef.current) return;
            if (restoringRef.current) applyTarget();
            else record();
        };
        const stop = (): void => {
            reveal();
            if (!restoringRef.current) return;
            restoringRef.current = false;
            window.cancelAnimationFrame(frame);
            record();
        };
        const onUserMove = (event: Event): void => {
            const el: HTMLElement | null = trackRef.current;
            if (el && event.target instanceof Node && el.contains(event.target)) stop();
        };
        document.addEventListener('scroll', onScroll, { capture: true, passive: true });
        document.addEventListener('touchstart', onUserMove, { capture: true, passive: true });
        document.addEventListener('pointerdown', onUserMove, { capture: true, passive: true });
        document.addEventListener('wheel', onUserMove, { capture: true, passive: true });
        if (restoringRef.current) {
            const deadline: number = performance.now() + HORIZONTAL_RESTORE_MS;
            // 프레임마다(그리기 직전) 확인한다 — 요소가 늦게 생기면 그때 숨기고 맞추고, 목표에 2 프레임 머물면 보인다
            const tick = (): void => {
                if (!restoringRef.current) return;
                hide();
                const el: HTMLElement | null = trackRef.current;
                const placed: boolean = applyTarget() && el !== null && Math.abs(el.scrollLeft - (targetRef.current ?? 0)) <= SCROLL_TOLERANCE_PX;
                settledFrames = placed ? settledFrames + 1 : 0;
                if (settledFrames >= 2) {
                    stop();
                    return;
                }
                if (performance.now() >= deadline) stop();
                else frame = window.requestAnimationFrame(tick);
            };
            applyTarget();
            frame = window.requestAnimationFrame(tick);
        }
        return () => {
            restoringRef.current = false;
            reveal();
            window.cancelAnimationFrame(frame);
            document.removeEventListener('scroll', onScroll, { capture: true });
            document.removeEventListener('touchstart', onUserMove, { capture: true });
            document.removeEventListener('pointerdown', onUserMove, { capture: true });
            document.removeEventListener('wheel', onUserMove, { capture: true });
        };
    }, [key, trackRef, applyTarget, hide, reveal]);

    return targetRef.current ?? 0;
}
